import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Mail,
  MoreHorizontal,
  Send,
} from "lucide-react";
import type { AvailableColumn } from "@/api/types";
import { resolveSortFieldForColumn, type SortSpec } from "./filterTypes";
import {
  COMPANY_COLUMN_ALIASES,
  buildGenericCompanyColumn,
} from "@/lib/companyColumnRenderers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type ColumnDef,
  type ColumnSizingState,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MotionTableRow } from "@/components/ui/motion";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TagBadge } from "@/components/tags/TagBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatRelativeDate, initials } from "@/lib/formatters";
import { getStatusColor, scoreBg, scoreColor } from "@/lib/constants";
import { isUsGrowthHistory } from "@/lib/usGrowth";
import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";
import type { Company, PipelineStatus, ProjectMember, User } from "@/api/types";

interface CompanyTableProps {
  items: Company[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  currentUser: User | null;
  members: ProjectMember[];
  /** Effective pipeline statuses for color/label resolution. */
  pipelineStatuses: PipelineStatus[];
  showScore?: boolean;
  /** When false, the Tags column is hidden (no tags.view perm). */
  showTags?: boolean;
  projectSlug: string;
  /** When false, the row checkbox column is hidden (no bulk perms). */
  selectable?: boolean;
  /** When provided, renders a per-row "..." dropdown with an "Archive" item. */
  onAskArchive?: (company: Company) => void;
  /** When provided, the per-row dropdown also renders a "Start outreach" item. */
  onAskEnroll?: (company: Company) => void;
  /** Ordered list of backend column keys (from GET /columns) to render
   *  between the select checkbox and the row-actions column. When
   *  omitted, the legacy hardcoded set is used (back-compat for the
   *  Clients / Archived tabs). */
  visibleColumnKeys?: string[];
  /** Configuration for backend-supplied columns — used to render keys
   *  that don't have a dedicated rich renderer. Required when
   *  visibleColumnKeys references a key we don't recognize natively. */
  availableColumns?: AvailableColumn[];
  /** Per-column widths, keyed by column id. The page owns this state
   *  so it can persist to localStorage / saved views. Omit for an
   *  uncontrolled table using each column's default `size`. */
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (next: ColumnSizingState) => void;
  /** Multi-sort state (F3-bis-frontend). When provided alongside
   *  `onSortsChange`, sortable column headers (per
   *  resolveSortFieldForColumn) become clickable. Click cycles
   *  asc → desc → unsorted on that field. Shift+click appends as
   *  secondary. Omit both for a non-sortable table — current
   *  Clients/Archived consumers leave them undefined. */
  sorts?: SortSpec[];
  onSortsChange?: (next: SortSpec[]) => void;
}

export function CompanyTable({
  items,
  selectedIds,
  onSelectionChange,
  currentUser,
  members,
  pipelineStatuses,
  showScore = true,
  showTags = false,
  projectSlug,
  selectable = true,
  onAskArchive,
  onAskEnroll,
  visibleColumnKeys,
  availableColumns,
  columnSizing,
  onColumnSizingChange,
  sorts,
  onSortsChange,
}: CompanyTableProps) {
  const navigate = useNavigate();

  // Sort dispatch — implements the F3-bis-frontend D8 semantics:
  //   - plain click on a sortable header cycles
  //     unsorted → asc → desc → unsorted on that field
  //   - Shift+click appends the field at the end of the sort list
  //     (or cycles its dir if already present, keeping its position)
  //   - clicking a non-sortable column id is a no-op (the caller
  //     gates this via resolveSortFieldForColumn but we double-check)
  const handleHeaderSort = (columnId: string, shiftKey: boolean) => {
    if (!sorts || !onSortsChange) return;
    const field = resolveSortFieldForColumn(columnId);
    if (!field) return;
    const existingIdx = sorts.findIndex((s) => s.field === field);
    if (existingIdx === -1) {
      // Field not in sorts yet. Plain click inserts as PRIMARY
      // (the user's last action wins on the leading-sort slot);
      // Shift+click appends as secondary.
      const fresh: SortSpec = { field, dir: "asc" };
      onSortsChange(shiftKey ? [...sorts, fresh] : [fresh, ...sorts]);
      return;
    }
    const current = sorts[existingIdx];
    if (current.dir === "asc") {
      // asc → desc, preserve position
      onSortsChange(
        sorts.map((s, i) => (i === existingIdx ? { ...s, dir: "desc" } : s)),
      );
      return;
    }
    // desc → remove from sorts (cycle to unsorted)
    onSortsChange(sorts.filter((_, i) => i !== existingIdx));
  };

  /** For a given column id, resolve { dir, priority } if it's
   *  currently in the active sort list. Priority is 1-indexed
   *  for the badge ("1", "2", …) and only rendered when there
   *  are 2+ sorts (single-sort needn't be numbered). */
  const headerSortState = (
    columnId: string,
  ): { dir: "asc" | "desc"; priority: number | null } | null => {
    if (!sorts || sorts.length === 0) return null;
    const field = resolveSortFieldForColumn(columnId);
    if (!field) return null;
    const idx = sorts.findIndex((s) => s.field === field);
    if (idx === -1) return null;
    return {
      dir: sorts[idx].dir,
      priority: sorts.length > 1 ? idx + 1 : null,
    };
  };

  const sortEnabled = !!(sorts && onSortsChange);

  const rowSelection = useMemo<RowSelectionState>(() => {
    const out: RowSelectionState = {};
    for (const id of selectedIds) out[String(id)] = true;
    return out;
  }, [selectedIds]);

  const columns = useMemo<ColumnDef<Company>[]>(() => {
    const cols: ColumnDef<Company>[] = [];
    if (selectable) {
      cols.push({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(value) =>
              table.toggleAllRowsSelected(value === true)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableResizing: false,
        size: 36,
      });
    }
    cols.push(
      {
        id: "company",
        accessorKey: "name",
        header: "Company",
        // Default width — the user can drag to fit their data. The
        // cell already truncates internally (`min-w-0` + `truncate`).
        size: 200,
        minSize: 80,
        maxSize: 400,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-bold text-slate-600">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                {/* line-clamp-2 lets long names wrap to a second
                 *  line when the operator narrows the column, instead
                 *  of being chopped with an ellipsis after one. */}
                <div className="line-clamp-2 break-words font-medium text-slate-900">
                  {c.name}
                </div>
                {c.domain && (
                  <div className="truncate text-xs text-slate-400">
                    {c.domain}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "sector",
        accessorKey: "sector",
        header: "Sector",
        size: 140,
        minSize: 60,
        maxSize: 250,
        cell: ({ getValue }) => (
          <span className="line-clamp-2 break-words text-sm text-slate-500">
            {(getValue<string | null>() as string | null) ?? "—"}
          </span>
        ),
      },
    );

    if (showScore) {
      cols.push({
        id: "score",
        accessorKey: "effective_score",
        header: "Score",
        size: 90,
        minSize: 50,
        maxSize: 120,
        cell: ({ row }) => {
          if (isUsGrowthHistory(row.original.metadata)) {
            return (
              <span
                className="text-xs text-slate-600"
                title="Ouvrez la société pour consulter son score, ses preuves et son état provisoire."
              >
                Diagnostic CFO
              </span>
            );
          }
          return (
            <ScoreCell
              score={row.original.effective_score}
              overridden={row.original.score_override !== null}
            />
          );
        },
      });
    }

    cols.push({
      id: "status",
      accessorKey: "pipeline_status",
      header: "Status",
      size: 140,
      minSize: 60,
      maxSize: 220,
      cell: ({ row, getValue }) => {
        const status = getValue<string>();
        const colors = getStatusColor(status, pipelineStatuses);
        const label =
          pipelineStatuses.find((s) => s.key === status)?.label ??
          status.replace(/_/g, " ");
        const outreach = row.original.outreach;
        return (
          <div className="inline-flex items-center gap-1.5">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={status}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                  colors.bg,
                  colors.text,
                  colors.border,
                )}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", colors.dot)} />
                <span className="capitalize">{label}</span>
              </motion.span>
            </AnimatePresence>
            {outreach?.is_in_campaign && (
              <OutreachBadge
                step={outreach.current_step ?? null}
                total={outreach.total_steps ?? null}
              />
            )}
          </div>
        );
      },
    });

    if (showTags) {
      cols.push({
        id: "tags",
        header: "Tags",
        enableSorting: false,
        size: 220,
        minSize: 60,
        maxSize: 360,
        cell: ({ row }) => {
          const tags = row.original.tags;
          if (!tags || tags.length === 0) {
            return <span className="text-xs text-slate-300">—</span>;
          }
          // Wrap pills onto a second row; cap visible height at ~2
          // rows of pills so wide rows don't push the table down.
          // Hover the cell to see the full set in a tooltip.
          return (
            <div
              className="flex max-h-[44px] flex-wrap items-center gap-1 overflow-hidden"
              title={tags.map((t) => t.name).join(", ")}
            >
              {tags.map((t) => (
                <TagBadge key={t.id} tag={t} size="sm" />
              ))}
            </div>
          );
        },
      });
    }

    cols.push(
      {
        id: "owner",
        accessorKey: "owner_id",
        header: "Owner",
        size: 140,
        minSize: 60,
        maxSize: 220,
        cell: ({ getValue }) => {
          const ownerId = getValue<string | null>();
          if (!ownerId) {
            return <span className="text-xs text-slate-400">Unassigned</span>;
          }
          const member = members.find((m) => m.id === ownerId);
          const isMe = currentUser?.id === ownerId;
          const fullName =
            member?.full_name ?? (isMe ? currentUser?.full_name : null);

          if (!fullName) {
            return (
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6 border border-slate-200">
                  <AvatarFallback className="bg-slate-300 text-[10px] font-semibold text-white">
                    ?
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-slate-400" title={ownerId}>
                  Unknown user
                </span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6 border border-slate-200">
                <AvatarFallback className="bg-slate-900 text-[10px] font-semibold text-white">
                  {initials(fullName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-slate-600">{fullName}</span>
            </div>
          );
        },
      },
      {
        id: "updated",
        accessorKey: "updated_at",
        header: "Updated",
        size: 110,
        minSize: 60,
        maxSize: 180,
        cell: ({ getValue }) => (
          <span className="text-xs text-slate-400">
            {formatRelativeDate(getValue<string>())}
          </span>
        ),
      },
    );

    if (onAskArchive || onAskEnroll) {
      cols.push({
        id: "row_actions",
        header: "",
        enableSorting: false,
        enableResizing: false,
        size: 36,
        cell: ({ row }) => {
          const c = row.original;
          const canEnroll =
            !!onAskEnroll &&
            !c.outreach?.is_in_campaign &&
            c.has_email_contact !== false;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label={`More actions for ${c.name}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-44"
                onClick={(e) => e.stopPropagation()}
              >
                {canEnroll && (
                  <DropdownMenuItem
                    className="text-indigo-700 focus:bg-indigo-50 focus:text-indigo-700"
                    onSelect={() => onAskEnroll!(c)}
                  >
                    <Send className="mr-2 h-3.5 w-3.5" />
                    Start outreach
                  </DropdownMenuItem>
                )}
                {onAskArchive && (
                  <DropdownMenuItem
                    className="text-amber-700 focus:bg-amber-50 focus:text-amber-700"
                    onSelect={() => onAskArchive(c)}
                  >
                    <Archive className="mr-2 h-3.5 w-3.5" />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      });
    }

    return cols;
  }, [
    selectable,
    showScore,
    showTags,
    currentUser,
    members,
    pipelineStatuses,
    onAskArchive,
    onAskEnroll,
  ]);

  // When the parent supplies a visibleColumnKeys list (from GET
  // /projects/{slug}/columns + user prefs), filter and reorder the
  // columns built above to match. The select column (if selectable)
  // always stays first; row_actions (if provided) always stays last.
  // Backend keys without a dedicated rich renderer fall through to a
  // generic ColumnDef driven by the AvailableColumn config.
  const finalColumns = useMemo<ColumnDef<Company>[]>(() => {
    if (!visibleColumnKeys) return columns;
    const byKey = new Map<string, ColumnDef<Company>>();
    // Register every existing column under its own id AND under any
    // backend-key aliases the catalog might use, so a key like "owner"
    // (the human-readable id from GET /columns) and a key like
    // "owner_id" (the actual Company attribute) both route to the same
    // rich renderer. Aliases live in a shared map so ClientsTable
    // resolves the same way.
    for (const c of columns) {
      const id = c.id;
      if (!id) continue;
      byKey.set(id, c);
      const aliases = COMPANY_COLUMN_ALIASES[id];
      if (aliases) for (const a of aliases) byKey.set(a, c);
    }
    const out: ColumnDef<Company>[] = [];
    const selectCol = columns.find((c) => c.id === "select");
    if (selectCol) out.push(selectCol);
    const seen = new Set<string>(["select", "row_actions"]);
    for (const key of visibleColumnKeys) {
      if (seen.has(key)) continue;
      seen.add(key);
      const c = byKey.get(key);
      if (c) {
        out.push(c);
        continue;
      }
      const fallback = buildGenericCompanyColumn(
        key,
        availableColumns,
        members,
        currentUser,
      );
      if (fallback) out.push(fallback);
    }
    const rowActions = columns.find((c) => c.id === "row_actions");
    if (rowActions) out.push(rowActions);
    return out;
  }, [columns, visibleColumnKeys, availableColumns, members, currentUser]);

  const table = useReactTable({
    data: items,
    columns: finalColumns,
    state: { rowSelection, ...(columnSizing ? { columnSizing } : {}) },
    enableRowSelection: selectable,
    // `onChange` lets the user drag and see the column resize live
    // rather than waiting until they let go. The parent receives the
    // updated map after every increment.
    columnResizeMode: "onChange",
    getRowId: (row) => String(row.id),
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      const ids = new Set<number>();
      for (const [id, selected] of Object.entries(next)) {
        if (selected) ids.add(Number(id));
      }
      onSelectionChange(ids);
    },
    onColumnSizingChange: onColumnSizingChange
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(columnSizing ?? {})
              : updater;
          onColumnSizingChange(next);
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-plane bg-white shadow-plane">
      <Table
        // `minWidth` (not `width`) — the base Table component is
        // already `w-full`, so this fills the container when the
        // columns sum is narrower, and starts horizontal scroll once
        // the user drags them past the container.
        style={{ minWidth: `${table.getCenterTotalSize()}px` }}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="border-slate-900/[0.06] hover:bg-transparent"
            >
              {headerGroup.headers.map((header) => {
                // Sortability is derived from the column id, NOT
                // from TanStack's `getCanSort` — the backend's
                // allow-list is the source of truth and three
                // utility columns (select/tags/actions) carry
                // enableSorting:false anyway. resolveSortFieldForColumn
                // returns null for non-sortable ids.
                const sortInfo = sortEnabled
                  ? headerSortState(header.column.id)
                  : null;
                const canSortThis =
                  sortEnabled &&
                  resolveSortFieldForColumn(header.column.id) !== null;
                const onHeaderClick = canSortThis
                  ? (e: MouseEvent<HTMLTableCellElement>) => {
                      // Ignore clicks that originated on the resize
                      // handle (it already calls stopPropagation,
                      // belt-and-braces).
                      if (
                        (e.target as HTMLElement).getAttribute("role") ===
                        "separator"
                      ) {
                        return;
                      }
                      handleHeaderSort(header.column.id, e.shiftKey);
                    }
                  : undefined;
                return (
                  <TableHead
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                    onClick={onHeaderClick}
                    aria-sort={
                      sortInfo
                        ? sortInfo.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={cn(
                      "group relative text-[11px] font-medium uppercase tracking-wider text-slate-500",
                      canSortThis &&
                        "cursor-pointer transition-colors hover:bg-slate-50 hover:text-slate-700",
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {sortInfo && (
                        <span
                          className="inline-flex items-center gap-0.5 text-slate-600"
                          aria-hidden
                        >
                          {sortInfo.dir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )}
                          {sortInfo.priority !== null && (
                            <span className="rounded-full bg-slate-200 px-1 text-[9px] font-semibold leading-none text-slate-700">
                              {sortInfo.priority}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={() => header.column.resetSize()}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize column (double-click to reset)"
                        // Translate the handle 50% right so half sits
                        // over the next column — doubles the hit area
                        // and matches how spreadsheets feel.
                        style={{ transform: "translateX(50%)" }}
                        className={cn(
                          "absolute right-0 top-0 z-10 h-full w-3 cursor-col-resize select-none touch-none",
                          // Visible on header hover so the affordance
                          // is discoverable; brighter while dragging.
                          "bg-transparent transition-colors",
                          "group-hover:bg-indigo-200",
                          header.column.getIsResizing() && "bg-indigo-500",
                        )}
                      />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          <AnimatePresence initial={false} mode="popLayout">
            {table.getRowModel().rows.map((row, idx) => (
              <MotionTableRow
                key={row.id}
                index={idx}
                data-state={row.getIsSelected() ? "selected" : undefined}
                onClick={() =>
                  navigate(
                    `/projects/${projectSlug}/companies/${row.original.id}`,
                  )
                }
                className={cn(
                  "group cursor-pointer border-slate-900/[0.06] transition-colors duration-150 motion-reduce:transition-none",
                  row.getIsSelected()
                    ? "bg-indigo-500/[0.07] hover:bg-indigo-500/[0.1]"
                    : "hover:bg-indigo-500/[0.04]",
                )}
              >
                {row.getVisibleCells().map((cell) => {
                  // The select column owns the entire cell's hit area —
                  // clicking the cell padding (not just the checkbox)
                  // toggles selection without bubbling up to the row's
                  // navigation. The Checkbox itself already calls
                  // stopPropagation on its own onClick, so the toggle
                  // here only fires for clicks on the surrounding pad.
                  const isSelectCell = cell.column.id === "select";
                  return (
                    <TableCell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}px` }}
                      // No overflow-hidden — line-clamp and the inner
                      // truncate/max-h handlers each clip on their own;
                      // hiding at the cell level would chop multi-line
                      // text back to a single row.
                      className={cn(
                        "py-3 align-top text-sm",
                        isSelectCell && "cursor-pointer",
                      )}
                      onClick={
                        isSelectCell
                          ? (e) => {
                              e.stopPropagation();
                              row.toggleSelected(!row.getIsSelected());
                            }
                          : undefined
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </MotionTableRow>
            ))}
          </AnimatePresence>
        </TableBody>
      </Table>
    </div>
  );
}

function OutreachBadge({
  step,
  total,
}: {
  step: number | null;
  total: number | null;
}) {
  const tooltip =
    step && total
      ? `Email sequence active · Step ${step}/${total}`
      : "Email sequence active";
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center text-indigo-500"
            aria-label={tooltip}
          >
            <Mail className="h-3 w-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ScoreCell({
  score,
  overridden,
}: {
  score: number | null;
  overridden: boolean;
}) {
  const target = score ?? 0;
  const display = useAnimatedNumber(target, 300);
  // Track previous to flash a tint when score moves.
  const [flash, setFlash] = useFlashOnChange(target);

  return (
    <div className="flex items-center gap-2">
      <motion.span
        animate={{
          color:
            flash === "up"
              ? "#10b981"
              : flash === "down"
                ? "#f43f5e"
                : undefined,
        }}
        transition={{ duration: 0.5 }}
        className={cn(
          "w-7 text-right text-sm font-medium tabular-nums",
          flash === null && scoreColor(score),
        )}
        onAnimationComplete={() => setFlash(null)}
      >
        {score == null ? "—" : Math.round(display)}
      </motion.span>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
        <motion.div
          className={cn("h-full rounded-full", scoreBg(score))}
          animate={{ width: `${Math.min(score ?? 0, 100)}%` }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      </div>
      {overridden && (
        <span
          className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-amber-700"
          title="Manual override"
        >
          M
        </span>
      )}
    </div>
  );
}

/**
 * Returns ["up" | "down" | null, setter] — flips to "up"/"down" when `value`
 * changes, then the consumer clears it after the flash transition completes.
 */
function useFlashOnChange(value: number) {
  const [flash, setFlash] = useState<"up" | "down" | null>(null);
  const prev = useRef(value);
  useEffect(() => {
    if (value > prev.current) setFlash("up");
    else if (value < prev.current) setFlash("down");
    prev.current = value;
  }, [value]);
  return [flash, setFlash] as const;
}

/**
 * Skeleton renders the same Table / TableHead / TableRow / TableCell DOM as
 * the real CompanyTable so column widths line up exactly during the loading
 * → loaded swap. Mirroring the structure (not just the looks) is what
 * eliminates the layout shift the user sees.
 */
export function CompanyTableSkeleton({
  rows = 8,
  showScore = true,
  showTags = false,
  selectable = true,
}: {
  rows?: number;
  showScore?: boolean;
  showTags?: boolean;
  selectable?: boolean;
}) {
  // Mirrors the column set used by the real table.
  return (
    <div className="overflow-x-auto rounded-plane bg-white shadow-plane">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-900/[0.06] hover:bg-transparent">
            {selectable && (
              <TableHead
                style={{ width: 36 }}
                className="text-[11px] font-medium uppercase tracking-wider text-slate-500"
              >
                <Skeleton className="h-4 w-4" />
              </TableHead>
            )}
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Company
            </TableHead>
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Sector
            </TableHead>
            {showScore && (
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Score
              </TableHead>
            )}
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Status
            </TableHead>
            {showTags && (
              <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Tags
              </TableHead>
            )}
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Owner
            </TableHead>
            <TableHead className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Updated
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i} className="border-slate-900/[0.06]">
              {selectable && (
                <TableCell className="py-3">
                  <Skeleton className="h-4 w-4" />
                </TableCell>
              )}
              <TableCell className="py-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-3">
                <Skeleton className="h-3.5 w-20" />
              </TableCell>
              {showScore && (
                <TableCell className="py-3">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-3.5 w-7" />
                    <Skeleton className="h-1.5 w-20 rounded-full" />
                  </div>
                </TableCell>
              )}
              <TableCell className="py-3">
                <Skeleton className="h-5 w-24 rounded-full" />
              </TableCell>
              {showTags && (
                <TableCell className="py-3">
                  <Skeleton className="h-5 w-16 rounded-full" />
                </TableCell>
              )}
              <TableCell className="py-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
              </TableCell>
              <TableCell className="py-3">
                <Skeleton className="h-3 w-14" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
